import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { ShieldCheck, Save, RefreshCw, PlayCircle, Loader2, CheckCircle2, AlertCircle, KeyRound } from "lucide-react";
import { toast } from "sonner";

/**
 * v1.32 — BK Insurance Roster live-sync configuration panel.
 * Pulls the up-to-date insured-teacher list from a partner API and mirrors
 * it into `insured_bk_teachers` so enrollment-time verification stays fresh.
 */
export default function BkRosterPanel() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const load = async () => {
    try { setCfg((await api.get(`/verification/insured-bk/live-config`)).data); }
    catch { toast.error("Failed to load config"); }
  };
  useEffect(() => { load(); }, []);

  const patch = (k, v) => setCfg((c) => ({ ...c, [k]: v }));
  const patchMap = (k, v) => setCfg((c) => ({ ...c, field_map: { ...(c.field_map || {}), [k]: v } }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...cfg };
      if (payload.auth_token === "••••••") delete payload.auth_token;
      const { data } = await api.post(`/verification/insured-bk/live-config`, payload);
      setCfg(data);
      toast.success("Saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const runSync = async (dry = false) => {
    setSyncing(true);
    try {
      const { data } = await api.post(`/verification/insured-bk/sync-live`, { dry_run: dry });
      toast.success(`${dry ? "Dry run" : "Sync"} complete: +${data.inserted}, ~${data.updated}, expired ${data.expired}`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Sync failed"); }
    finally { setSyncing(false); }
  };

  if (!cfg) return <Card><CardContent className="p-6 text-sm text-muted-foreground flex items-center gap-2"><Loader2 className="animate-spin" size={14}/> Loading…</CardContent></Card>;

  const last = cfg.last_sync_at ? new Date(cfg.last_sync_at).toLocaleString() : "never";
  const stats = cfg.last_sync_stats || {};
  const statusOk = (cfg.last_sync_status || "").startsWith("ok");

  return (
    <Card>
      <CardContent className="p-6 space-y-5">
        <div className="flex items-start gap-3">
          <ShieldCheck className="text-emerald-600" />
          <div>
            <div className="font-semibold">BK Insurance Roster — Live Sync</div>
            <p className="text-xs text-muted-foreground max-w-xl">
              Point Medishiv at your partner's insured-teacher API. Every sync updates the
              matching list used at enrollment time. Stale rows (missing in the latest feed)
              are marked expired automatically so unregistered teachers can't slip through.
            </p>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <Switch checked={!!cfg.enabled} onCheckedChange={(v) => patch("enabled", v)} data-testid="bkroster-enabled" />
            <span className="text-xs">{cfg.enabled ? "Auto-sync ON" : "Auto-sync OFF"}</span>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-3">
          <Field label="API URL (GET, returns JSON list)" value={cfg.url} onChange={(v) => patch("url", v)}
                 placeholder="https://partner.example.com/api/v1/insured-teachers" testid="bkroster-url" />
          <Field label="HTTP Method" value={cfg.method} onChange={(v) => patch("method", v)} placeholder="GET" testid="bkroster-method" />
          <Field label="Auth Header Name" value={cfg.auth_header} onChange={(v) => patch("auth_header", v)} placeholder="Authorization" testid="bkroster-authhdr" />
          <Field label="Auth Scheme (blank if none)" value={cfg.auth_scheme} onChange={(v) => patch("auth_scheme", v)} placeholder="Bearer" testid="bkroster-authscheme" />
          <Field label="Auth Token / API Key" value={cfg.auth_token} onChange={(v) => patch("auth_token", v)}
                 placeholder={cfg.auth_token_present ? "•••••• (leave blank to keep)" : "paste your token"} secret testid="bkroster-authtoken" />
          <Field label="Data Path (dotted, optional)" value={cfg.data_path} onChange={(v) => patch("data_path", v)}
                 placeholder="e.g. data.results" testid="bkroster-datapath" />
          <Field label="Sync Interval (minutes)" value={cfg.sync_interval_minutes} onChange={(v) => patch("sync_interval_minutes", Number(v) || 60)}
                 placeholder="60" testid="bkroster-interval" />
        </div>

        <div className="pt-2">
          <div className="text-xs uppercase text-slate-500 mb-2 flex items-center gap-1"><KeyRound size={11}/> Field mapping — how the partner's JSON maps to our schema</div>
          <div className="grid md:grid-cols-3 gap-3">
            {["name", "unique_id", "city", "state", "phone", "insurance_validity"].map((k) => (
              <Field key={k} label={k === "insurance_validity" ? "insurance_validity (YYYY-MM-DD)" : k}
                     value={(cfg.field_map || {})[k]} onChange={(v) => patchMap(k, v)}
                     placeholder={k} testid={`bkroster-map-${k}`} />
            ))}
          </div>
        </div>

        <div className="flex items-center justify-between border-t pt-4">
          <div className="text-xs text-slate-500">
            <div>Last sync: <strong>{last}</strong>
              <Badge className={`ml-2 ${statusOk ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>
                {statusOk ? <CheckCircle2 size={11} className="mr-1 inline" /> : <AlertCircle size={11} className="mr-1 inline" />}
                {cfg.last_sync_status || "no history"}
              </Badge>
            </div>
            {stats && Object.keys(stats).length > 0 && (
              <div className="mt-1">
                Inserted: {stats.inserted ?? 0} · Updated: {stats.updated ?? 0} · Expired: {stats.expired ?? 0} · Active: {stats.total_active ?? "—"}
              </div>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => runSync(true)} disabled={syncing} data-testid="bkroster-dryrun">
              {syncing ? <Loader2 className="animate-spin" size={14}/> : <PlayCircle size={14}/>} Dry run
            </Button>
            <Button variant="outline" onClick={() => runSync(false)} disabled={syncing} data-testid="bkroster-sync">
              {syncing ? <Loader2 className="animate-spin" size={14}/> : <RefreshCw size={14}/>} Sync now
            </Button>
            <Button onClick={save} disabled={saving} data-testid="bkroster-save">
              {saving ? <Loader2 className="animate-spin" size={14}/> : <Save size={14}/>} Save
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function Field({ label, value, onChange, placeholder, secret, testid }) {
  return (
    <div className="space-y-1">
      <div className="text-[11px] uppercase text-slate-500">{label}</div>
      <Input type={secret ? "password" : "text"} placeholder={placeholder}
             value={value ?? ""} onChange={(e) => onChange(e.target.value)}
             data-testid={testid} />
    </div>
  );
}
